// BeginnerInlineRunner — runs the skill's micro-prompt RIGHT INSIDE the
// Try pane so beginners actually use AI on every lesson instead of just
// reading about it. Streams from the public `home-demo-chat` edge fn
// (no auth wall, IP-rate-limited) so first-time visitors get an
// instant "I just made AI do something" moment.
//
// Doing-density ramps with skill number:
//   • #1–4   → editable prompt + Run button. Hand-holding copy.
//   • #5–9   → same, but the prompt is presented as a starter they
//              should tweak before running.
//   • #10+   → after running, surfaces a "Open Prompt Builder &
//              save to Library" CTA so the learner moves from
//              consumer to creator. Skill 10 is where the shell
//              flips into "you're building now" mode (matches the
//              user's product spec for the gamified arc).
//
// We deliberately reuse the unauthenticated demo endpoint instead of
// `hub-chat` so the runner works for signed-out visitors too — the
// whole point of Beginner Mode is the lowest possible friction first
// run. Signed-in users still hit the same endpoint here; the heavier
// quota'd Hub experience kicks in once they leave the shell.
import { useEffect, useRef, useState } from "react";
import { Loader2, Play, RotateCw, Sparkles, Wand2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { stashChatSeed } from "@/lib/chatSeedBus";

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/home-demo-chat`;
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Skill threshold at which the runner starts nudging users to build
// their own prompts in the Prompt Builder + save them to the Library.
// Mirrors the "by skill 10" milestone in the product spec.
export const BUILDER_UNLOCK_AT = 10;

interface Props {
  /** Mastery skill number — drives the doing-density ramp. */
  skillNum: number;
  /** Skill label, used to seed the Prompt Builder prefill. */
  skillLabel: string;
  /** Starter prompt from `masterySurfaceMap`. */
  starterPrompt: string;
  /** Called once the user has actually run the prompt + read output. */
  onRan?: () => void;
}

const systemFor = (skillNum: number) =>
  skillNum < BUILDER_UNLOCK_AT
    ? "You are a friendly AI coach for an absolute beginner. Reply in plain English, ≤6 short bullets or sentences, no jargon. End with one tiny suggestion they could try next."
    : "You are an AI coach helping a learner who is starting to build their own prompts. Give a useful answer AND finish with a 2-line note on how they could turn this into a reusable prompt template they'd save to their Library.";

export default function BeginnerInlineRunner({
  skillNum,
  skillLabel,
  starterPrompt,
  onRan,
}: Props) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(starterPrompt);
  const [output, setOutput] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasRun, setHasRun] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Reset when skill changes — the parent reuses this component across
  // skills via key, but be defensive in case it doesn't.
  useEffect(() => {
    setPrompt(starterPrompt);
    setOutput("");
    setError(null);
    setHasRun(false);
  }, [starterPrompt, skillNum]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const run = async () => {
    if (running || !prompt.trim()) return;
    setError(null);
    setOutput("");
    setRunning(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const resp = await fetch(ENDPOINT, {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: [
            { role: "system", content: systemFor(skillNum) },
            { role: "user", content: prompt.slice(0, 600) },
          ],
        }),
      });
      if (!resp.ok || !resp.body) {
        let msg = "Something went wrong. Try again in a moment.";
        try {
          const data = await resp.json();
          if (data?.error) msg = data.error;
        } catch {
          /* non-JSON */
        }
        if (resp.status === 429) {
          msg = "Free demo limit reached — sign up to keep going.";
        }
        setError(msg);
        setRunning(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let done = false;
      while (!done) {
        const { value, done: streamDone } = await reader.read();
        if (streamDone) break;
        buf += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buf.indexOf("\n")) !== -1) {
          let line = buf.slice(0, nl);
          buf = buf.slice(nl + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line || line.startsWith(":")) continue;
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") {
            done = true;
            break;
          }
          try {
            const parsed = JSON.parse(json);
            const delta = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (delta) setOutput((prev) => prev + delta);
          } catch {
            buf = line + "\n" + buf;
            break;
          }
        }
      }
      setHasRun(true);
      onRan?.();
    } catch (e) {
      if ((e as Error)?.name === "AbortError") return;
      console.error("[BeginnerInlineRunner]", e);
      setError("Network error. Check your connection and try again.");
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const isBuilderTier = skillNum >= BUILDER_UNLOCK_AT;
  const openBuilder = () => {
    // Seed the Hub via the existing chatSeedBus so the Builder lands
    // with the user's prompt prefilled — no big strings in the URL.
    stashChatSeed({
      primer: `You are helping a learner turn the prompt below into a reusable template they can save to their Library. The prompt comes from mastery skill #${skillNum}: "${skillLabel}".`,
      draft: prompt.trim(),
      source: `beginner:skill-${skillNum}`,
      title: skillLabel,
    });
    navigate(`/hub?tab=builder`);
  };

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          {skillNum < 5
            ? "Your prompt (we filled it in for you)"
            : skillNum < BUILDER_UNLOCK_AT
            ? "Tweak this prompt, then run"
            : "Write your own — we'll help you turn it into a reusable template"}
        </span>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          spellCheck
          className="mt-1 w-full resize-y rounded-md border border-border bg-background/80 p-2.5 text-xs font-mono text-foreground focus:border-primary focus:outline-none"
          placeholder="Type a prompt…"
          aria-label="Prompt to run"
        />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={run}
          disabled={running || !prompt.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-mono font-bold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60"
        >
          {running ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : hasRun ? (
            <RotateCw className="w-3.5 h-3.5" />
          ) : (
            <Play className="w-3.5 h-3.5" />
          )}
          {running ? "Running…" : hasRun ? "Run again" : "Run with AI"}
        </button>
        <span className="text-[10px] font-mono text-muted-foreground inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-primary" />
          You're using a real AI model — no signup needed
        </span>
      </div>

      {error && (
        <p className="text-[11px] font-mono text-destructive">{error}</p>
      )}

      {(output || running) && (
        <div className="rounded-md border border-border bg-background/60 p-3 text-sm leading-relaxed whitespace-pre-wrap text-foreground/90 font-sans min-h-[3rem]">
          {output || (
            <span className="text-xs font-mono text-muted-foreground">Streaming…</span>
          )}
        </div>
      )}

      {hasRun && isBuilderTier && (
        <div className="rounded-md border border-primary/40 bg-primary/5 p-3">
          <p className="text-[11px] font-mono text-foreground mb-2 leading-snug">
            🚀 You're past skill {BUILDER_UNLOCK_AT}. Time to <span className="font-bold text-primary">build</span> — turn this prompt into a reusable template and save it to your Library so future-you (and your friends) can fork it.
          </p>
          <button
            type="button"
            onClick={openBuilder}
            className="inline-flex items-center gap-1.5 rounded-md border border-primary/50 bg-primary/10 px-3 py-1.5 text-xs font-mono font-bold text-primary hover:bg-primary/20 transition-colors"
          >
            <Wand2 className="w-3.5 h-3.5" />
            Open Prompt Builder & save to Library
          </button>
        </div>
      )}
    </div>
  );
}